import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Camera } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { fadeUp } from '@/lib/motion';

const ARROW = 'absolute top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow hover:bg-white';

function ExportBadge() {
  return (
    <div className="absolute top-4 left-4">
      <span className="bg-white/90 backdrop-blur-sm text-saffron font-sans font-semibold text-xs px-3 py-1.5 rounded-md shadow">Export Grade</span>
    </div>
  );
}

function NoPhoto({ color = '#D4870A' }) {
  return (
    <motion.div
      initial="hidden" animate="visible" variants={fadeUp}
      className="relative overflow-hidden rounded-lg shadow-card-lift bg-gradient-to-br from-saffron-50/60 to-cream flex flex-col items-center justify-center aspect-[4/3]"
      data-testid="product-no-photo"
    >
      <div className="w-20 h-20 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: color + '20' }}>
        <Camera size={36} style={{ color }} className="opacity-40" />
      </div>
      <p className="text-earth/40 font-sans text-sm font-medium">Photos coming soon</p>
      <p className="text-earth/25 font-sans text-xs mt-1">Contact us for product samples</p>
    </motion.div>
  );
}

function Thumbnails({ photos, activeIdx, onSelect, productName }) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-1">
      {photos.map((photo, i) => (
        <button
          key={photo}
          onClick={() => onSelect(i)}
          className={`flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition-all ${i === activeIdx ? 'border-saffron shadow-saffron-glow' : 'border-transparent opacity-60 hover:opacity-100'}`}
          data-testid={`photo-thumb-${i}`}
        >
          <img src={photo} alt={`${productName} thumbnail ${i + 1}`} width={64} height={64} loading="lazy" className="w-full h-full object-cover" />
        </button>
      ))}
    </div>
  );
}

function Carousel({ photos, productName }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const count = photos.length;
  return (
    <motion.div initial="hidden" animate="visible" variants={fadeUp} className="space-y-3" data-testid="product-photo-gallery">
      <div className="relative overflow-hidden rounded-lg shadow-card-lift group">
        <AnimatePresence mode="wait">
          <motion.img
            key={activeIdx}
            src={photos[activeIdx]}
            alt={`${productName} - Photo ${activeIdx + 1}`}
            width={800}
            height={600}
            className="w-full aspect-[4/3] object-cover"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
            data-testid="product-main-photo"
          />
        </AnimatePresence>
        <ExportBadge />
        <div className="absolute top-4 right-4">
          <span className="bg-black/40 backdrop-blur-sm text-white font-sans text-[10px] px-2 py-1 rounded">{activeIdx + 1} / {count}</span>
        </div>
        <button onClick={() => setActiveIdx(i => (i - 1 + count) % count)} className={`${ARROW} left-3`} data-testid="photo-prev">
          <ChevronLeft size={18} className="text-earth-dark" />
        </button>
        <button onClick={() => setActiveIdx(i => (i + 1) % count)} className={`${ARROW} right-3`} data-testid="photo-next">
          <ChevronRight size={18} className="text-earth-dark" />
        </button>
      </div>
      <Thumbnails photos={photos} activeIdx={activeIdx} onSelect={setActiveIdx} productName={productName} />
    </motion.div>
  );
}

export default function PhotoGallery({ photos, productName, color }) {
  if (photos.length === 0) return <NoPhoto color={color || undefined} />;
  if (photos.length === 1) {
    return (
      <motion.div initial="hidden" animate="visible" variants={fadeUp} className="relative overflow-hidden rounded-lg shadow-card-lift">
        <img src={photos[0]} alt={productName} width={800} height={600} className="w-full aspect-[4/3] object-cover" data-testid="product-main-photo" />
        <ExportBadge />
      </motion.div>
    );
  }
  return <Carousel photos={photos} productName={productName} />;
}
